
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, UserCheck, ArrowRight } from "lucide-react";

export interface MentorData {
  id: string;
  name: string;
  profession: string;
  location: string;
  avatar: string;
  specialties: string[];
}

interface RecommendedMentorsListProps {
  isLoading: boolean;
  mentors: MentorData[];
  onViewProfile: (mentorId: string) => void;
  onNavigate: (route: string) => void;
}

const RecommendedMentorsList = ({ 
  isLoading, 
  mentors, 
  onViewProfile, 
  onNavigate 
}: RecommendedMentorsListProps) => {
  return (
    <div className="mb-10">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Recommended Mentors</h2>
        <Button 
          variant="outline" 
          onClick={() => onNavigate('/search-mentors')}
          className="text-emu-blue border-emu-blue hover:bg-emu-blue/10"
        >
          View All <ArrowRight size={16} className="ml-1" />
        </Button>
      </div>
      
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center gap-4 mb-4">
                <Skeleton className="h-16 w-16 rounded-full" />
                <div>
                  <Skeleton className="h-5 w-32 mb-2" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
              <div className="space-y-2 mb-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                <Skeleton className="h-6 w-16 rounded-full" />
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>
              <Skeleton className="h-9 w-full" />
            </div>
          ))}
        </div>
      ) : mentors.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {mentors.map((mentor) => (
            <div key={mentor.id} className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <Avatar className="w-16 h-16 border-2 border-emu-blue">
                    <AvatarImage src={mentor.avatar} alt={mentor.name} />
                    <AvatarFallback>{mentor.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold">{mentor.name}</h3>
                    <div className="flex items-center text-sm text-gray-600 mb-1">
                      <UserCheck size={14} className="mr-1" />
                      {mentor.profession}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin size={14} className="mr-1" />
                      {mentor.location}
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-1 mt-4">
                  {mentor.specialties.slice(0, 3).map((specialty, idx) => (
                    <Badge 
                      key={idx} 
                      variant="outline"
                      className="bg-emu-blue/10 text-emu-blue border-emu-blue/30 text-xs"
                    >
                      {specialty}
                    </Badge>
                  ))}
                  {mentor.specialties.length > 3 && (
                    <Badge variant="outline" className="bg-gray-100 text-gray-500 text-xs">
                      +{mentor.specialties.length - 3} more
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="bg-gray-50 border-t p-4">
                <Button 
                  className="w-full bg-emu-blue hover:bg-emu-blue/90"
                  onClick={() => onViewProfile(mentor.id)}
                >
                  View Profile
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
          <p className="text-gray-600 mb-4">No recommended mentors available at the moment.</p>
          <Button 
            onClick={() => onNavigate('/search-mentors')}
            className="bg-emu-blue hover:bg-emu-blue/90"
          >
            Search for Mentors
          </Button>
        </div>
      )}
    </div>
  );
};

export default RecommendedMentorsList;
